import BigNumber from 'bignumber.js';

import type { ChainKey } from '@infinex/chain-config';

/**
 * Solana rent-exempt minimum for a native SOL account, in integer lamports
 * (890,880 lamports = 0.00089088 SOL). This is the canonical value: the front-end and
 * the backend transfer validators both compare against this BigInt, which avoids
 * floating-point comparison error near the rent boundary.
 */
export const SOL_RENT_EXEMPT_MINIMUM_LAMPORTS = 890_880n;

export function isValidRemainingSolBalanceLamports(
  remainingLamports: bigint
): boolean {
  return (
    remainingLamports === 0n ||
    remainingLamports >= SOL_RENT_EXEMPT_MINIMUM_LAMPORTS
  );
}

export const MON_MINIMUM_BALANCE = 10;

export const MON_MINIMUM_BALANCE_ERROR_MESSAGE =
  'Insufficient remaining balance. Minimum 10 MON must remain in account.';

export function isValidRemainingMonBalance(
  currentBalance: number,
  remainingBalance: number
): boolean {
  // Rule only applies if user currently has MON
  if (currentBalance === 0) return true;
  // If they have MON, remaining must be >= 10 (strict: no zero balance once you have MON)
  return remainingBalance >= MON_MINIMUM_BALANCE;
}

// Pretty sure this is the best place for this?
// TODO: Refactor other USD_DECIMALS references to point here
export const USD_DECIMALS = 6;

export const UNKNOWN_TOKEN_SYMBOL = 'UNKNOWN';
export const UNKNOWN_TOKEN_NAME = 'Unknown';
export const UNKNOWN_TOKEN_DECIMALS = 0;

export const UNKNOWN_TOKEN_URL =
  'https://infinex.xyz/assets/assets/unknown-token.svg';

export const UNKNOWN_NFT_URL =
  'https://infinex.xyz/assets/assets/unknown-nft.svg';

// Convert a string or bigint USD value to dollar a float
export function usdToNumber(usd: string | bigint): number {
  return Number.parseFloat(usd.toString()) / 10 ** USD_DECIMALS;
}

export function splitOnFirstUnderscore(str: string) {
  return str.split(/_(.*)/s);
}

/**
 * Returns the maximum amount that can be moved for an asset,
 * accounting for chain-specific reserve requirements.
 *
 * Rules:
 * - Monad native (MON): Must leave 10 MON minimum in account
 * - All other assets (including Solana): Full balance can be moved
 *
 * @param chain - The chain key (e.g., 'monad', 'solana', 'ethereum')
 * @param tokenAddress - The token address ('native' for native tokens)
 * @param balance - The current balance as BigNumber
 * @returns The maximum moveable amount as BigNumber
 */
export function getMaxMoveAmount(
  chain: ChainKey,
  tokenAddress: string,
  balance: BigNumber
): BigNumber {
  // Monad native: must leave 10 MON minimum
  if (chain === 'monad' && tokenAddress === 'native') {
    return BigNumber.max(0, balance.minus(MON_MINIMUM_BALANCE));
  }

  // All other assets: full balance
  return balance;
}
